import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	LinearProgress,
	Tab,
	Tabs,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { GlobalState } from "context/GlobalContext";
import axios from "axios";

const SPREADSHEET_LOGS_ENDPOINT = "/api/pos_reco/get_pos_file_upload_logs";
const PAY_BY_LINK_LOGS_ENDPOINT = "/api/pos_reco/get_pay_by_link_image_upload_logs";
const FILE_RERUN_ENDPOINT = "/cash-card-upload/rerun";

const LOG_TABS = {
	SPREADSHEET: "spreadsheet",
	PAY_BY_LINK: "pay_by_link",
};

const ellipsisSx = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	fontWeight: 500,
	width: "100%",
};

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleString();
};

const getStatusColor = (status) => {
	const normalized = String(status || "").toUpperCase();
	if (normalized.includes("SUCCESS") || normalized.includes("COMPLETED") || normalized === "UPLOADED") return "success";
	if (normalized.includes("PENDING") || normalized.includes("IN_PROGRESS") || normalized.includes("JOB_TRIGGERED")) return "warning";
	if (normalized.includes("FAILED") || normalized.includes("ERROR")) return "error";
	return "default";
};

const StatusChip = ({ status }) => {
	const label = status ? String(status).toUpperCase() : "UNKNOWN";
	return (
		<Chip
			label={label}
			color={getStatusColor(label)}
			size="small"
			variant="outlined"
			sx={{ fontWeight: 600, borderRadius: "6px" }}
		/>
	);
};

const UploadLogsTable = ({ columns, data, loading, globalFilter, onGlobalFilterChange, emptyMessage }) => {
	const [sorting, setSorting] = useState([]);

	const table = useReactTable({
		data,
		columns,
		state: { sorting, globalFilter },
		onSortingChange: setSorting,
		onGlobalFilterChange,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 25 } },
	});

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: 1,
					px: 2,
					py: 1.25,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}>
				<TextField
					size="small"
					variant="outlined"
					placeholder="Search..."
					value={globalFilter ?? ""}
					onChange={(event) => onGlobalFilterChange(event.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchRoundedIcon fontSize="small" />
							</InputAdornment>
						),
						sx: { borderRadius: 1.5, backgroundColor: "grey.50" },
					}}
					sx={{ minWidth: 220 }}
				/>
			</Box>
			{loading && <LinearProgress />}
			<TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
				<Table stickyHeader size="small">
					<TableHead>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									const sortDirection = header.column.getIsSorted();
									return (
										<TableCell
											key={header.id}
											align="center"
											sortDirection={sortDirection || false}
											sx={{
												backgroundColor: "grey.50",
												fontWeight: 500,
												color: "text.secondary",
												textTransform: "uppercase",
												fontSize: "0.7rem",
												letterSpacing: "0.4px",
												whiteSpace: "nowrap",
											}}>
											{header.column.getCanSort() ? (
												<TableSortLabel
													active={!!sortDirection}
													direction={sortDirection || "asc"}
													onClick={header.column.getToggleSortingHandler()}>
													{flexRender(header.column.columnDef.header, header.getContext())}
												</TableSortLabel>
											) : (
												flexRender(header.column.columnDef.header, header.getContext())
											)}
										</TableCell>
									);
								})}
							</TableRow>
						))}
					</TableHead>
					<TableBody>
						{table.getRowModel().rows.length === 0 ? (
							<TableRow>
								<TableCell colSpan={columns.length} align="center">
									<Typography variant="body2" sx={{ my: 2 }} color="text.secondary">
										{loading ? "Loading..." : emptyMessage}
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							table.getRowModel().rows.map((row) => (
								<TableRow key={row.id} hover>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id} align="center" sx={{ fontSize: "0.82rem" }}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				count={table.getFilteredRowModel().rows.length}
				page={table.getState().pagination.pageIndex}
				onPageChange={(event, newPage) => table.setPageIndex(newPage)}
				rowsPerPage={table.getState().pagination.pageSize}
				onRowsPerPageChange={(event) => table.setPageSize(Number(event.target.value))}
				rowsPerPageOptions={[10, 25, 50, 100]}
				sx={{ borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}
			/>
		</Box>
	);
};

const FileDetailsDialogue = ({ open, onClose, initialTab = LOG_TABS.SPREADSHEET }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [activeTab, setActiveTab] = useState(initialTab);
	const [spreadsheetRows, setSpreadsheetRows] = useState([]);
	const [payByLinkRows, setPayByLinkRows] = useState([]);
	const [spreadsheetLoading, setSpreadsheetLoading] = useState(false);
	const [payByLinkLoading, setPayByLinkLoading] = useState(false);
	const [spreadsheetFilter, setSpreadsheetFilter] = useState("");
	const [payByLinkFilter, setPayByLinkFilter] = useState("");
	const [rerunId, setRerunId] = useState(null);

	const normalizeRows = (payload) => {
		const data = Array.isArray(payload) ? payload : payload?.result || payload?.files || [];
		return data.map((row, index) => ({
			...row,
			id: row?.id ?? index + 1,
		}));
	};

	const fetchSpreadsheetLogs = useCallback(async () => {
		setSpreadsheetLoading(true);
		try {
			const response = await axios.get(SPREADSHEET_LOGS_ENDPOINT);
			setSpreadsheetRows(normalizeRows(response.data));
		} catch (error) {
			setSpreadsheetRows([]);
		} finally {
			setSpreadsheetLoading(false);
		}
	}, []);

	const fetchPayByLinkLogs = useCallback(async () => {
		setPayByLinkLoading(true);
		try {
			const response = await axios.get(PAY_BY_LINK_LOGS_ENDPOINT);
			setPayByLinkRows(normalizeRows(response.data));
		} catch (error) {
			setPayByLinkRows([]);
		} finally {
			setPayByLinkLoading(false);
		}
	}, []);

	const fetchAllLogs = useCallback(async () => {
		await Promise.all([fetchSpreadsheetLogs(), fetchPayByLinkLogs()]);
	}, [fetchSpreadsheetLogs, fetchPayByLinkLogs]);

	useEffect(() => {
		if (!open) return;
		setActiveTab(initialTab);
		fetchAllLogs();
	}, [open, initialTab, fetchAllLogs]);

	const handleRerun = async (row) => {
		setRerunId(row.id);
		try {
			await pythonVmInstance.post(FILE_RERUN_ENDPOINT, {
				id: row.id,
				file_type: row.file_type,
				file_name: row.file_name,
			});
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Rerun triggered successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			await fetchSpreadsheetLogs();
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable to rerun file",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setRerunId(null);
		}
	};

	const spreadsheetColumns = useMemo(
		() => [
			{
				accessorKey: "file_type",
				header: "File Type",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx} title={info.getValue() || "-"}>
						{info.getValue() || "-"}
					</Typography>
				),
			},
			{
				accessorKey: "file_name",
				header: "File Name",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx} title={info.getValue() || "-"}>
						{info.getValue() || "-"}
					</Typography>
				),
			},
			{
				accessorKey: "date",
				header: "Date",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx}>
						{formatDateTime(info.getValue())}
					</Typography>
				),
			},
			{
				accessorKey: "reason",
				header: "Reason",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx} title={info.getValue() || "-"}>
						{info.getValue() || "-"}
					</Typography>
				),
			},
			{
				accessorKey: "status",
				header: "Status",
				cell: (info) => <StatusChip status={info.getValue()} />,
			},
			{
				id: "actions",
				header: "Rerun",
				enableSorting: false,
				cell: ({ row }) => (
					<Button
						size="small"
						variant="text"
						startIcon={
							rerunId === row.original.id ? (
								<CircularProgress size={14} color="inherit" />
							) : (
								<RefreshRoundedIcon fontSize="small" />
							)
						}
						onClick={() => handleRerun(row.original)}
						disabled={rerunId === row.original.id}
						sx={{ textTransform: "none", minWidth: "auto" }}>
						Rerun
					</Button>
				),
			},
		],
		[rerunId],
	);

	const payByLinkColumns = useMemo(
		() => [
			{
				accessorKey: "file_name",
				header: "Image Name",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx} title={info.getValue() || "-"}>
						{info.getValue() || "-"}
					</Typography>
				),
			},
			{
				accessorKey: "uploaded_by",
				header: "Uploaded By",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx} title={info.getValue() || "-"}>
						{info.getValue() || "-"}
					</Typography>
				),
			},
			{
				id: "uploaded_at",
				accessorFn: (row) => row.upload_date || row.created_at,
				header: "Uploaded At",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx}>
						{formatDateTime(info.getValue())}
					</Typography>
				),
			},
			{
				accessorKey: "row_count",
				header: "Rows",
				cell: (info) => {
					const value = info.getValue();
					return (
						<Typography variant="body2" sx={ellipsisSx}>
							{value === null || value === undefined ? "-" : value}
						</Typography>
					);
				},
			},
			{
				accessorKey: "job_id",
				header: "Job ID",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx} title={info.getValue() || "-"}>
						{info.getValue() || "-"}
					</Typography>
				),
			},
			{
				accessorKey: "status",
				header: "Status",
				cell: (info) => <StatusChip status={info.getValue()} />,
			},
			{
				accessorKey: "reason",
				header: "Reason",
				cell: (info) => (
					<Typography variant="body2" sx={ellipsisSx} title={info.getValue() || "-"}>
						{info.getValue() || "-"}
					</Typography>
				),
			},
		],
		[],
	);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
				<Box>
					<Typography variant="h6" fontWeight={600}>
						Upload Logs
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Review spreadsheet uploads and Pay By Link image uploads separately.
					</Typography>
				</Box>
				<IconButton onClick={onClose}>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ pb: 3, pt: 1 }}>
				<Tabs
					value={activeTab}
					onChange={(event, value) => setActiveTab(value)}
					sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
					<Tab
						value={LOG_TABS.SPREADSHEET}
						label={`Spreadsheet uploads (${spreadsheetRows.length})`}
						icon={<TableChartOutlinedIcon fontSize="small" />}
						iconPosition="start"
						sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
					/>
					<Tab
						value={LOG_TABS.PAY_BY_LINK}
						label={`Pay By Link images (${payByLinkRows.length})`}
						icon={<ImageOutlinedIcon fontSize="small" />}
						iconPosition="start"
						sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
					/>
				</Tabs>

				<Box
					sx={{
						height: "68vh",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						overflow: "hidden",
						bgcolor: "background.paper",
					}}>
					{activeTab === LOG_TABS.SPREADSHEET ? (
						<UploadLogsTable
							columns={spreadsheetColumns}
							data={spreadsheetRows}
							loading={spreadsheetLoading}
							globalFilter={spreadsheetFilter}
							onGlobalFilterChange={setSpreadsheetFilter}
							emptyMessage="No spreadsheet uploads found"
						/>
					) : (
						<UploadLogsTable
							columns={payByLinkColumns}
							data={payByLinkRows}
							loading={payByLinkLoading}
							globalFilter={payByLinkFilter}
							onGlobalFilterChange={setPayByLinkFilter}
							emptyMessage="No Pay By Link image uploads found"
						/>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export { LOG_TABS };
export default FileDetailsDialogue;
